import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { productsContent } from '@/mocks/home';

function useInView<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, inView };
}

export default function ProductsSection() {
  const { ref: sectionRef, inView } = useInView<HTMLDivElement>();

  return (
    <section className="relative bg-background-100 py-16 md:py-24 overflow-hidden" ref={sectionRef}>
      <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
        {/* Title */}
        <div className={`mb-12 md:mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h3 className="text-lg md:text-xl font-semibold text-primary-400 tracking-wide">
            {productsContent.titleCn}
          </h3>
          <h2 className="mt-1 text-3xl md:text-4xl lg:text-5xl font-black text-foreground-50 font-heading uppercase tracking-wider">
            {productsContent.titleEn}
          </h2>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {productsContent.items.map((item, index) => (
            <Link
              key={item.id}
              to={item.href}
              className={`group relative block bg-background-50 rounded-lg overflow-hidden border border-background-200/50 hover:border-primary-500/40 transition-all duration-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${150 + index * 120}ms` }}
            >
              <div className="p-6 md:p-8">
                {/* Icon */}
                <div className="w-14 h-14 flex items-center justify-center rounded-lg bg-background-100 text-primary-400 mb-5 group-hover:bg-primary-500 group-hover:text-foreground-50 transition-colors duration-300">
                  <i className={`${item.icon} text-2xl`} />
                </div>

                {/* Text */}
                <h4 className="text-lg md:text-xl font-bold text-foreground-50 group-hover:text-primary-400 transition-colors">
                  {item.titleCn}
                </h4>
                <p className="mt-1 text-sm text-foreground-400 font-label">
                  {item.titleEn}
                </p>
                <p className="mt-3 text-sm text-foreground-300 leading-relaxed">
                  {item.desc}
                </p>

                {/* Arrow */}
                <div className="mt-6 flex items-center gap-2 text-foreground-400 group-hover:text-primary-400 transition-colors">
                  <span className="text-sm font-medium">了解更多</span>
                  <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform" />
                </div>
              </div>

              {/* Bottom accent line */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}